import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isNil } from 'ramda'

import FeatureToggle from '~/constants/featureToggle'
import {
  getConstantsIsLoading,
  getFeatureToggle,
} from '~/store/reducers/constants'

type BusinessLike = {
  id: string
}

type UseCanaryCookieSyncParams = {
  assignedBusinesses: BusinessLike[]
  businessIdFromUrl: string | null
  currentBusinessId?: string
  currentUserId?: string | null
  isBusinessIdMismatch: boolean
  isBusinessIdUrlParamEnabled: boolean
}

const CANARY_COOKIE_NAME = 'canary'
const CANARY_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60 // 30 days

const getCanaryCookieValue = () =>
  document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${CANARY_COOKIE_NAME}=`))
    ?.split('=')[1]

const isCanaryCookieEnabled = () => getCanaryCookieValue() === 'true'

const setCanaryCookie = () => {
  document.cookie = `${CANARY_COOKIE_NAME}=true; path=/; Max-Age=${CANARY_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
}

const removeCanaryCookie = () => {
  document.cookie = `${CANARY_COOKIE_NAME}=; path=/; Max-Age=0; SameSite=Lax`
}

export const useCanaryCookieSync = ({
  assignedBusinesses,
  businessIdFromUrl,
  currentBusinessId,
  currentUserId,
  isBusinessIdUrlParamEnabled,
  isBusinessIdMismatch,
}: UseCanaryCookieSyncParams) => {
  const isFeatureToggleTestCanaryCookieEnabled = useSelector(
    getFeatureToggle(FeatureToggle.TEST_CANARY_COOKIE),
  )
  const constantsAreLoading = useSelector(getConstantsIsLoading)

  const isSyncEnabled = !isNil(currentUserId) && isBusinessIdUrlParamEnabled
  const isUrlSyncedWithCurrentBusiness =
    !isSyncEnabled || businessIdFromUrl === currentBusinessId

  const hasAccessToCurrentBusiness = useMemo(
    () =>
      Boolean(
        currentBusinessId &&
          assignedBusinesses?.some(({ id }) => id === currentBusinessId),
      ),
    [assignedBusinesses, currentBusinessId],
  )

  useEffect(() => {
    if (
      !currentBusinessId ||
      isBusinessIdMismatch ||
      !hasAccessToCurrentBusiness ||
      constantsAreLoading ||
      !isUrlSyncedWithCurrentBusiness
    ) {
      return
    }

    const isCookieEnabled = isCanaryCookieEnabled()

    if (isFeatureToggleTestCanaryCookieEnabled && !isCookieEnabled) {
      setCanaryCookie()
      if (!isCanaryCookieEnabled()) {
        return
      }
      window.location.reload()
    } else if (!isFeatureToggleTestCanaryCookieEnabled && isCookieEnabled) {
      removeCanaryCookie()
      if (isCanaryCookieEnabled()) {
        return
      }
      window.location.reload()
    }
  }, [
    currentBusinessId,
    isBusinessIdMismatch,
    hasAccessToCurrentBusiness,
    constantsAreLoading,
    isUrlSyncedWithCurrentBusiness,
    isFeatureToggleTestCanaryCookieEnabled,
  ])
}
